import { useEffect, useMemo, useState } from "react";
import { useLocation } from "react-router-dom";
import cx from "classnames";

import type { UseBundleReturn } from "types/offerBundle";
import type { OfferPlan } from "types/offerPlan";

import t, { AsHtml } from "utils/translate";
import { useBundle } from "utils/useBundle";
import getAidFromCookie from "utils/getAidFromCookie";
import getRswUrl from "utils/getRswUrl";
import { pageView } from "utils/analytics";
import { CLINICAL_PROGRAM } from "utils/resultFunctions/constants";
import daProps from "utils/daPropsPricingBtn";

import {
  MODAL_DESCRIPTION,
  NONE,
  TREATMENT
} from "components/ClinicCheckout/constants";
import useAppContext from "components/AppContext";
import applangaKeyMap from "components/ClinicCheckout/Plans/components/MedsCard/applangaKeyMap";
import Column from "components/Column";
import SiteWrapper from "components/SiteWrapper";
import SimpleOfferCard from "components/ClinicCheckout/Plans/components/OfferCards/Simple";
import Loading from "components/Loading";
import Error from "components/ClinicCheckout/Error";
import MedsCard from "components/ClinicCheckout/Plans/components/MedsCard";
import useUrlSlugContext from "components/UrlSlugContext";

import styles from "components/ClinicCheckout/Plans/styles.module.scss";

export const ResultsUni5ClinicalPlans: React.FC = () => {
  const { urlSlug } = useUrlSlugContext();
  const { search } = useLocation();
  const { newQuiz } = useAppContext();
  const { baseUrl } = useUrlSlugContext();

  const urlParams = new URLSearchParams(search || "");
  const treatment = urlParams.get(TREATMENT) || "";
  const { disclaimers } = applangaKeyMap[treatment] || applangaKeyMap[NONE];

  const { bundle, plans, isLoading, isError, retry }: UseBundleReturn =
    useBundle({
      bundleId: t("SPECIAL_OFFER_JS_CLINIC_BUNDLE_ID")
    });
  const [selectedPlan, setSelectedPlan] = useState<OfferPlan>();

  useEffect(() => {
    if (bundle && plans.length) {
      let plan = plans.find(p => p.id === bundle.preSelectedOfferPlanId);

      if (!plan) {
        plan = plans[0];
      }

      setSelectedPlan(plan);
    }
  }, [bundle, plans]);

  useEffect(() => {
    pageView("plans");
  }, []);

  const onSelection = plan => {
    setSelectedPlan(plan);
  };

  const checkoutUrl = useMemo(() => {
    const assessmentId = getAidFromCookie(newQuiz, baseUrl);
    return getRswUrl(
      selectedPlan?.id,
      bundle?.id,
      assessmentId,
      urlSlug,
      CLINICAL_PROGRAM,
      true
    );
  }, [selectedPlan?.id, bundle?.id]);

  const renderContent = () => {
    if (applangaKeyMap[treatment]) {
      return <MedsCard urlParam={treatment} />;
    }

    return (
      <Column>
        <h2 className={styles.heading}>
          {t("CLINIC_CHECKOUT_PROGRAM_HEADING")}
        </h2>
        <MedsCard />
      </Column>
    );
  };

  return (
    <Loading loading={isLoading}>
      {!isError ? (
        <SiteWrapper size="small">
          <Column className={cx(styles.content, styles.noPadding)}>
            {renderContent()}
            <Column className={styles.planSlice}>
              <h2 className={styles.heading}>{t("CHECKOUT_CHOOSE_PLAN")}</h2>
              <AsHtml value={bundle?.contentMap?.get(MODAL_DESCRIPTION)} />
              {plans.map((plan, index) => (
                <SimpleOfferCard
                  key={plan.id}
                  plan={plan}
                  onSelection={onSelection}
                  selected={selectedPlan?.id === plan.id}
                  addTerms={index === 0}
                />
              ))}
            </Column>
            {disclaimers && (
              <div className={styles.disclaimer}>
                {disclaimers.map(disclaimer => (
                  <AsHtml key={disclaimer} value={t(disclaimer)} />
                ))}
              </div>
            )}
            <a
              className={styles.cta}
              href={checkoutUrl}
              data-testid="continue-pricing"
              {...daProps(selectedPlan)}
            >
              {t("CONTINUE_WITH").replace(
                "{{planName}}",
                selectedPlan?.contentMap.get("OFFER_PLAN_NAME")?.toLowerCase()
              )}
            </a>
          </Column>
        </SiteWrapper>
      ) : (
        <Error onClick={retry as any} />
      )}
    </Loading>
  );
};
